import {ok, err, type Result} from 'framework';
import type {AuthClient} from '@/infrastructure/auth/auth-client';
import type {
  AuthRepository,
  ChangePasswordData,
  RequestPasswordResetData,
  ResetPasswordData,
  SignInWithEmailData,
  SignUpWithEmailData,
} from '../application/ports/auth.repository';
import type {Session} from '../domain/session';
import {
  ChangePasswordFailedError,
  CredentialAccountNotFoundError,
  EmailAlreadyInUseError,
  EmailNotVerifiedError,
  InvalidCredentialsError,
  InvalidCurrentPasswordError,
  InvalidResetTokenError,
  PasswordTooLongError,
  PasswordTooShortError,
  RequestPasswordResetFailedError,
  ResetPasswordFailedError,
  SignUpFailedError,
  UnknownAuthError,
  type ChangePasswordError,
  type GetSessionError,
  type RequestPasswordResetError,
  type ResetPasswordError,
  type SignInError,
  type SignOutError,
  type SignUpError,
} from '../domain/errors';
import type {BetterAuthError, BetterAuthUser} from './validators/better-auth-response.schemas';
import type {BetterAuthSignInResponseValidator} from './validators/better-auth-sign-in-response.validator';
import type {BetterAuthSignUpResponseValidator} from './validators/better-auth-sign-up-response.validator';
import type {BetterAuthSignOutResponseValidator} from './validators/better-auth-sign-out-response.validator';
import type {BetterAuthGetSessionResponseValidator} from './validators/better-auth-get-session-response.validator';
import type {BetterAuthChangePasswordResponseValidator} from './validators/better-auth-change-password-response.validator';
import type {BetterAuthRequestPasswordResetResponseValidator} from './validators/better-auth-request-password-reset-response.validator';
import type {BetterAuthResetPasswordResponseValidator} from './validators/better-auth-reset-password-response.validator';

const mapUserToSession = (user: BetterAuthUser): Session => ({
  user: {
    id: user.id,
    email: user.email,
    name: user.name,
    image: user.image ?? null,
  },
});

const mapSignInError = (error: BetterAuthError): SignInError => {
  if (error.status === 401) {
    return new InvalidCredentialsError();
  }

  if (error.status === 403) {
    return new EmailNotVerifiedError();
  }

  return new UnknownAuthError(error.message ?? 'Unknown sign-in error.');
};

const mapSignUpError = (error: BetterAuthError): SignUpError => {
  if (error.code === 'USER_ALREADY_EXISTS' || error.status === 422) {
    return new EmailAlreadyInUseError();
  }

  return new SignUpFailedError(error.message ?? 'Sign-up failed.');
};

const mapChangePasswordError = (error: BetterAuthError): ChangePasswordError => {
  if (error.code === 'INVALID_PASSWORD') {
    return new InvalidCurrentPasswordError();
  }

  if (error.code === 'PASSWORD_TOO_SHORT') {
    return new PasswordTooShortError(error.message ?? 'The new password is too short.');
  }

  if (error.code === 'PASSWORD_TOO_LONG') {
    return new PasswordTooLongError(error.message ?? 'The new password is too long.');
  }

  if (error.code === 'CREDENTIAL_ACCOUNT_NOT_FOUND') {
    return new CredentialAccountNotFoundError();
  }

  return new ChangePasswordFailedError(error.message ?? 'Password change failed.');
};

/**
 * Anti-enumeration: treat any completed HTTP response with status <= 500 as success.
 * Fail only on status > 500, or errors without a usable HTTP status (transport).
 */
const isRequestPasswordResetServerFailure = (error: BetterAuthError): boolean => {
  if (typeof error.status === 'number') {
    return error.status > 500;
  }

  return true;
};

const mapResetPasswordError = (error: BetterAuthError): ResetPasswordError => {
  if (error.code === 'INVALID_TOKEN') {
    return new InvalidResetTokenError(error.message);
  }

  if (error.code === 'PASSWORD_TOO_SHORT') {
    return new PasswordTooShortError(error.message ?? 'The new password is too short.');
  }

  if (error.code === 'PASSWORD_TOO_LONG') {
    return new PasswordTooLongError(error.message ?? 'The new password is too long.');
  }

  return new ResetPasswordFailedError(error.message ?? 'Password reset failed.');
};

export class BetterAuthAuthRepository implements AuthRepository {
  constructor(
    private readonly authClient: AuthClient,
    private readonly signInResponseValidator: BetterAuthSignInResponseValidator,
    private readonly signUpResponseValidator: BetterAuthSignUpResponseValidator,
    private readonly signOutResponseValidator: BetterAuthSignOutResponseValidator,
    private readonly getSessionResponseValidator: BetterAuthGetSessionResponseValidator,
    private readonly changePasswordResponseValidator: BetterAuthChangePasswordResponseValidator,
    private readonly requestPasswordResetResponseValidator: BetterAuthRequestPasswordResetResponseValidator,
    private readonly resetPasswordResponseValidator: BetterAuthResetPasswordResponseValidator,
  ) {}

  async signInWithEmail(data: SignInWithEmailData): Promise<Result<Session, SignInError>> {
    const raw = await this.authClient.signIn.email({
      email: data.email,
      password: data.password,
    });

    const validated = this.signInResponseValidator.validate(raw);

    if (!validated.ok) {
      return err(new UnknownAuthError(validated.error.message));
    }

    if (validated.data.error) {
      return err(mapSignInError(validated.data.error));
    }

    if (!validated.data.data) {
      return err(new UnknownAuthError('Sign-in returned no session.'));
    }

    return ok(mapUserToSession(validated.data.data.user));
  }

  async signUpWithEmail(data: SignUpWithEmailData): Promise<Result<Session, SignUpError>> {
    const raw = await this.authClient.signUp.email({
      email: data.email,
      password: data.password,
      name: data.name,
    });

    const validated = this.signUpResponseValidator.validate(raw);

    if (!validated.ok) {
      return err(new UnknownAuthError(validated.error.message));
    }

    if (validated.data.error) {
      return err(mapSignUpError(validated.data.error));
    }

    if (!validated.data.data) {
      return err(new SignUpFailedError('Sign-up returned no session.'));
    }

    return ok(mapUserToSession(validated.data.data.user));
  }

  async changePassword(
    data: ChangePasswordData,
  ): Promise<Result<undefined, ChangePasswordError>> {
    const raw = await this.authClient.changePassword({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      revokeOtherSessions: data.revokeOtherSessions,
    });

    const validated = this.changePasswordResponseValidator.validate(raw);

    if (!validated.ok) {
      return err(new UnknownAuthError(validated.error.message));
    }

    if (validated.data.error) {
      return err(mapChangePasswordError(validated.data.error));
    }

    return ok(undefined);
  }

  async requestPasswordReset(
    data: RequestPasswordResetData,
  ): Promise<Result<undefined, RequestPasswordResetError>> {
    let raw: unknown;
    try {
      raw = await this.authClient.requestPasswordReset({
        email: data.email,
        redirectTo: data.redirectTo,
      });
    } catch (cause) {
      const message =
        cause instanceof Error
          ? cause.message
          : 'We could not send a password reset email right now.';
      return err(new RequestPasswordResetFailedError(message));
    }

    const validated = this.requestPasswordResetResponseValidator.validate(raw);

    if (!validated.ok) {
      return err(new UnknownAuthError(validated.error.message));
    }

    if (validated.data.error && isRequestPasswordResetServerFailure(validated.data.error)) {
      return err(
        new RequestPasswordResetFailedError(
          validated.data.error.message ??
            'We could not send a password reset email right now.',
        ),
      );
    }

    // Anti-enumeration: status <= 500 (or success body) always succeeds.
    return ok(undefined);
  }

  async resetPassword(
    data: ResetPasswordData,
  ): Promise<Result<undefined, ResetPasswordError>> {
    const raw = await this.authClient.resetPassword({
      newPassword: data.newPassword,
      token: data.token,
    });

    const validated = this.resetPasswordResponseValidator.validate(raw);

    if (!validated.ok) {
      return err(new UnknownAuthError(validated.error.message));
    }

    if (validated.data.error) {
      return err(mapResetPasswordError(validated.data.error));
    }

    return ok(undefined);
  }

  async signOut(): Promise<Result<undefined, SignOutError>> {
    const raw = await this.authClient.signOut();

    const validated = this.signOutResponseValidator.validate(raw);

    if (!validated.ok) {
      return err(new UnknownAuthError(validated.error.message));
    }

    if (validated.data.error) {
      return err(new UnknownAuthError(validated.data.error.message ?? 'Sign-out failed.'));
    }

    return ok(undefined);
  }

  async getSession(): Promise<Result<Session | null, GetSessionError>> {
    const raw = await this.authClient.getSession();

    const validated = this.getSessionResponseValidator.validate(raw);

    if (!validated.ok) {
      return err(new UnknownAuthError(validated.error.message));
    }

    if (validated.data.error) {
      return err(new UnknownAuthError(validated.data.error.message ?? 'Failed to fetch session.'));
    }

    if (!validated.data.data) {
      return ok(null);
    }

    return ok(mapUserToSession(validated.data.data.user));
  }
}
